import React, { useState } from 'react';
import { X, ShoppingBag } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { toast } from '../hooks/use-toast';
import CheckoutForm from './CheckoutForm';
import { CartItem } from './CartItem';

const ShoppingCart = () => {
  const {
    cart,
    isCartOpen,
    setIsCartOpen,
    updateQuantity,
    removeFromCart,
    getCartTotal,
    clearCart
  } = useCart();
  const [showCheckout, setShowCheckout] = useState(false);

  if (!isCartOpen) return null;

  const handleRemove = (productId, productName) => {
    removeFromCart(productId);
    toast({
      title: 'Removed from cart',
      description: `${productName} has been removed from your cart.`,
    });
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
        onClick={() => setIsCartOpen(false)}
      />

      {/* Cart Drawer */}
      <div className="fixed right-0 top-0 bottom-0 w-full sm:w-96 bg-white shadow-2xl z-50 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <ShoppingBag className="h-6 w-6 text-[#001B2A]" />
            <h2 className="text-xl font-serif font-bold text-[#001B2A]">
              Shopping Cart
            </h2>
          </div>
          <button
            onClick={() => setIsCartOpen(false)}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="h-6 w-6 text-[#001B2A]" />
          </button>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-6">
          {cart.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <ShoppingBag className="h-16 w-16 text-gray-300 mb-4" />
              <p className="text-gray-500 text-lg">Your cart is empty</p>
              <p className="text-gray-400 text-sm mt-2">Add some timeless pieces to get started</p>
            </div>
          ) : (
            <div className="space-y-4">
              {cart.map((item) => (
                <CartItem
                  key={item.id}
                  item={item}
                  onUpdateQuantity={updateQuantity}
                  onRemove={handleRemove}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {cart.length > 0 && (
          <div className="border-t border-gray-200 p-6 space-y-4">
            <div className="flex items-center justify-between text-lg">
              <span className="font-medium text-gray-700">Subtotal:</span>
              <span data-testid="cart-subtotal" className="font-bold text-[#001B2A] text-2xl">
                ${getCartTotal().toFixed(2)}
              </span>
            </div>
            <p className="text-xs text-gray-500">+ $9.99 flat rate shipping, added at checkout</p>

            <button
              onClick={() => setShowCheckout(true)}
              data-testid="proceed-to-checkout-button"
              className="w-full bg-[#001B2A] text-white py-4 rounded-md hover:bg-[#7B1E2B] transition-all duration-300 font-semibold shadow-lg hover:shadow-xl"
            >
              PROCEED TO CHECKOUT
            </button>
            <button
              onClick={clearCart}
              className="w-full bg-transparent border-2 border-gray-300 text-gray-700 py-3 rounded-md hover:bg-gray-100 transition-colors font-medium"
            >
              CLEAR CART
            </button>
          </div>
        )}
      </div>

      {/* Checkout Modal */}
      {showCheckout && (
        <CheckoutForm
          onClose={() => {
            setShowCheckout(false);
            setIsCartOpen(false);
          }}
        />
      )}
    </>
  );
};

export default ShoppingCart;
